/**
 * Boîte de réception des messages du formulaire Contact (eurodecor-meubles.be).
 * Table public.eds_contact_messages : lecture et suivi réservés aux rôles admin et gérant (RLS).
 * La liste ne charge jamais le corps du message ; seul le détail le charge.
 */
import { supabase } from './supabaseClient';

export type ContactReplyStatus = 'pending' | 'replied' | 'closed';

/** Colonnes affichées dans la liste (sans le corps du message). */
export interface ContactMessageListItem {
  id: string;
  created_at: string;
  name: string;
  email: string;
  store_name: string;
  subject: string;
  read_at: string | null;
  reply_status: ContactReplyStatus;
}

export interface ContactMessageDetail extends ContactMessageListItem {
  phone: string | null;
  message: string;
  consent_at: string;
  read_by: string | null;
  replied_at: string | null;
  replied_by: string | null;
}

export type ContactMessageFilter = 'all' | 'unread';

export const CONTACT_MESSAGES_PAGE_SIZE = 50;

const LIST_COLUMNS = 'id, created_at, name, email, store_name, subject, read_at, reply_status';
const DETAIL_COLUMNS = `${LIST_COLUMNS}, phone, message, consent_at, read_by, replied_at, replied_by`;

/** Identifiant auth.uid() de la session (≠ users.id de Luxori). */
async function getAuthUid(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) {
    throw new Error('Session expirée : reconnectez-vous.');
  }
  return data.user.id;
}

export const contactMessagesService = {
  /** Nombre de messages non lus (requête HEAD : aucune donnée personnelle ne transite). */
  async getUnreadCount(): Promise<number> {
    const { count, error } = await supabase
      .from('eds_contact_messages')
      .select('id', { count: 'exact', head: true })
      .is('read_at', null);
    if (error) throw error;
    return count ?? 0;
  },

  async list(
    filter: ContactMessageFilter = 'all',
    page = 0
  ): Promise<{ items: ContactMessageListItem[]; hasMore: boolean }> {
    const from = page * CONTACT_MESSAGES_PAGE_SIZE;
    let query = supabase
      .from('eds_contact_messages')
      .select(LIST_COLUMNS)
      .order('created_at', { ascending: false })
      // une ligne de plus pour savoir s'il reste une page
      .range(from, from + CONTACT_MESSAGES_PAGE_SIZE);
    if (filter === 'unread') query = query.is('read_at', null);
    const { data, error } = await query;
    if (error) throw error;
    const rows = (data ?? []) as ContactMessageListItem[];
    return {
      items: rows.slice(0, CONTACT_MESSAGES_PAGE_SIZE),
      hasMore: rows.length > CONTACT_MESSAGES_PAGE_SIZE,
    };
  },

  async getById(id: string): Promise<ContactMessageDetail | null> {
    const { data, error } = await supabase
      .from('eds_contact_messages')
      .select(DETAIL_COLUMNS)
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return (data as ContactMessageDetail | null) ?? null;
  },

  /** Marque lu : read_by = auth.uid() (le trigger de garde refuse tout autre identifiant). */
  async markRead(id: string): Promise<void> {
    const uid = await getAuthUid();
    const { error } = await supabase
      .from('eds_contact_messages')
      .update({ read_at: new Date().toISOString(), read_by: uid })
      .eq('id', id);
    if (error) throw error;
  },

  async markUnread(id: string): Promise<void> {
    const { error } = await supabase
      .from('eds_contact_messages')
      .update({ read_at: null, read_by: null })
      .eq('id', id);
    if (error) throw error;
  },
};
